<script lang="ts">
export default {
  name: 'KkSelectModal',
}
</script>

<script lang="ts" setup>
import { ref } from 'vue'
import { KkLoading, KkModal, KkButton, KkCheckbox } from '../../base'

export type SelectItem = {
  id: number;
  name: string;
}

export interface Props {
  isVisible: boolean
  headerLabel: string
  selectLabel: string
  list: SelectItem[]
  selectedList: SelectItem['id'][]
  loading?: boolean
}

export interface Emits {
  (key: 'close'): void
  (key: 'confirm', value: SelectItem['id'][]): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const selectingList = ref<SelectItem['id'][]>(props.selectedList)

const close = () => {
  emits('close')
}
const confirm = () => {
  emits('confirm', selectingList.value)
}

const handleSelect = (id: SelectItem['id']) => {
  if (selectingList.value.includes(id)) {
    selectingList.value = selectingList.value.filter(v => v !== id)
  } else {
    selectingList.value.push(id)
  }
}

const handleSelectedAll = () => {
  if (selectingList.value.length === props.list.length) {
    selectingList.value = []
  } else {
    selectingList.value = props.list.map(v => v.id)
  }
}
</script>

<template>
  <KkModal :is-visible="isVisible" width="720px" @change="close">
    <div class="content__header">
      {{ headerLabel }}
    </div>

    <div class="content__body">
      <div v-if="list" class="box">
        <div class="box__header">
          <p class="boxHeaderLabel">{{ selectLabel }}</p>
          <p>
            <KkCheckbox
              :id="`select_list_all`"
              :model-value="selectingList.length === props.list.length"
              :label="$t('labels.all')"
              @change="handleSelectedAll"
            />
          </p>
        </div>
        <div class="box__body">
          <ul class="list">
            <li
              v-for="( item, index ) in props.list"
              :key="index"
              class="list__item"
            >
              <KkCheckbox
                :id="`list_${index}`"
                :model-value="selectingList.includes(item.id)"
                :label="item.name"
                @change="handleSelect(item.id)"
              />
            </li>
          </ul>
        </div>
      </div>
      <KkLoading v-else />
    </div>

    <div class="content__footer">
      <KkButton
        class="footerCancelBtn"
        color="secondary"
        :text="$t('labels.cancel')"
        @click="close"
      />
      <KkButton
        color="primary"
        :text="$t('labels.confirm')"
        @click="confirm"
      />
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 24px 32px 16px;
    font-size: 20px;
    line-height: 1.5;
    border-bottom: 1px solid #E4E7EB;
  }
  &__body {
    height: 50vh;
    padding: 24px;
    overflow-y: scroll;
  }
  &__item {
    padding: 0 12px 12px;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    border-top: 1px solid #E4E7EB;
  }
}
.footerCancelBtn {
  margin-right: 8px;
}
.box {
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  &__header {
    display: flex;
    align-items: center;
    padding: 16px;
    background: #F5F6F9;
  }
  &__body {
    padding: 16px;
    overflow-y: scroll;
  }
}
.boxHeaderLabel {
  margin-right: 16px;
  font-weight: bold;
}
.list {
  display: flex;
  flex-wrap: wrap;
  &__item {
    margin: 8px;
  }
}
</style>
